import { FormattedText, ImageComponent, SpanTag } from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";
import React, { useState } from "react";

import {
  Card,
  CardCtaButtonList,
  CardCtaDivider,
  CardPadding,
  ContactBox,
  ContactBoxHeader,
  ContactGrid,
  ContactHeader,
  ContactLine,
  ContactMobileContent,
  ContactMobileSection,
  ContactMobileSectionHeader,
  ContactMobileTitle,
} from "../../OrderDetailV2.style";
import { useOrderDetailV2 } from "../../OrderDetailV2Context";
import type { OrderDetailV2ContactProps, TextNodeType } from "../../types";
import { AddAlternateContactModal } from "../AddAlternateContactModal";
import { TextButton } from "../TextButton";

const getTextValue = (node: TextNodeType | string | undefined): string => {
  if (!node) return "";
  if (typeof node === "string") return node;
  const { text } = node;
  if (!text) return "";
  if (typeof text === "string") return text;
  if (Array.isArray(text)) return text.map((t) => t.value ?? "").join("");
  return text.value ?? "";
};

export const OrderDetailV2Contact: React.FC<OrderDetailV2ContactProps> = ({
  sectionTitle,
  sectionIcon,
  mailIcon,
  callIcon,
  primarySection,
  alternateSection,
  formConfig,
  modalConfig,
  primaryEntity = null,
}: OrderDetailV2ContactProps) => {
  const { bookingId, refreshOrderDetails } = useOrderDetailV2();
  const device = useBreakpointV2();
  const [isAlternateContactModalOpen, setIsAlternateContactModalOpen] =
    useState(false);

  const alternateContact = alternateSection?.contact;
  const hasAlternateContact = Boolean(
    alternateContact?.emailNode || alternateContact?.contactNoNode
  );
  const isAlternateSectionVisible = alternateSection?.isVisible ?? false;
  const alternateAction = alternateSection?.action;
  const isActionEnabled = alternateAction?.isEnabled ?? false;
  const isActionVisible = alternateAction?.isVisible ?? false;

  const initialAlternateValues = hasAlternateContact
    ? {
        name: getTextValue(alternateContact?.nameNode),
        email: getTextValue(alternateContact?.emailNode),
        phone: getTextValue(alternateContact?.contactNoNode),
      }
    : undefined;

  const handleSaveAlternateContact = () => {};

  const openAlternateContactModal = () => {
    if (isActionEnabled && formConfig) {
      setIsAlternateContactModalOpen(true);
    }
  };

  if (!primarySection) {
    return null;
  }

  return (
    <Card>
      {device === "lg" ? (
        <CardPadding>
          {sectionTitle && (
            <ContactHeader>
              {sectionIcon && (
                <ImageComponent
                  src={sectionIcon}
                  width="20px"
                  height="20px"
                  margin="0 8px 0 0"
                />
              )}
              <SpanTag
                fontSize="large"
                fontWeight="bold"
                color={theme.greyText}
              >
                {sectionTitle}
              </SpanTag>
            </ContactHeader>
          )}

          <ContactGrid>
            <ContactBox>
              {primarySection.title && (
                <ContactBoxHeader>
                  <SpanTag fontSize="xs" color={theme.greyText7}>
                    {primarySection.title}
                  </SpanTag>
                  {primarySection.icon && (
                    <ImageComponent
                      src={primarySection.icon}
                      width="20px"
                      height="20px"
                    />
                  )}
                </ContactBoxHeader>
              )}

              <SpanTag fontSize="md" fontWeight="bold">
                <FormattedText value={primarySection.contact.nameNode} />
              </SpanTag>

              <ContactLine>
                <ImageComponent src={mailIcon} width="16px" height="16px" />
                <SpanTag fontSize="xs" color={theme.greyText}>
                  <FormattedText value={primarySection.contact.emailNode} />
                </SpanTag>
              </ContactLine>

              <ContactLine>
                <ImageComponent src={callIcon} width="16px" height="16px" />
                <SpanTag fontSize="xs" color={theme.greyText}>
                  <FormattedText value={primarySection.contact.contactNoNode} />
                </SpanTag>
              </ContactLine>
            </ContactBox>

            {isAlternateSectionVisible && alternateSection && (
              <ContactBox>
                {alternateSection.title && (
                  <ContactBoxHeader>
                    <SpanTag fontSize="xs" color={theme.greyText7}>
                      {alternateSection.title}
                    </SpanTag>
                    {alternateSection.icon && (
                      <ImageComponent
                        src={alternateSection.icon}
                        width="20px"
                        height="20px"
                      />
                    )}
                    {isActionVisible && alternateAction?.label && (
                      <TextButton
                        btnTxt={alternateAction.label}
                        onClick={openAlternateContactModal}
                        disabled={!isActionEnabled}
                        showDefaultRightIcon={false}
                      />
                    )}
                  </ContactBoxHeader>
                )}

                {hasAlternateContact ? (
                  <>
                    {alternateContact?.emailNode && (
                      <ContactLine>
                        <ImageComponent
                          src={mailIcon}
                          width="16px"
                          height="16px"
                        />
                        <SpanTag fontSize="xs" color={theme.greyText}>
                          <FormattedText value={alternateContact.emailNode} />
                        </SpanTag>
                      </ContactLine>
                    )}

                    {alternateContact?.contactNoNode && (
                      <ContactLine>
                        <ImageComponent
                          src={callIcon}
                          width="16px"
                          height="16px"
                        />
                        <SpanTag fontSize="xs" color={theme.greyText}>
                          <FormattedText
                            value={alternateContact.contactNoNode}
                          />
                        </SpanTag>
                      </ContactLine>
                    )}
                  </>
                ) : (
                  alternateSection.hint && (
                    <SpanTag fontSize="xs" color={theme.greyText7}>
                      {alternateSection.hint}
                    </SpanTag>
                  )
                )}
              </ContactBox>
            )}
          </ContactGrid>
        </CardPadding>
      ) : (
        <>
          {primarySection.title && (
            <ContactMobileSection zIndex={3}>
              <ContactMobileSectionHeader>
                <ContactMobileTitle>
                  <FormattedText value={primarySection.title} />
                </ContactMobileTitle>
              </ContactMobileSectionHeader>
              <ContactMobileContent>
                <SpanTag
                  fontSize="sm"
                  fontWeight="bold"
                  color={theme.blackText}
                >
                  <FormattedText value={primarySection.contact.nameNode} />
                </SpanTag>
                <ContactLine>
                  <ImageComponent src={mailIcon} width="16px" height="16px" />
                  <SpanTag fontSize="xs" color={theme.greyText}>
                    <FormattedText value={primarySection.contact.emailNode} />
                  </SpanTag>
                </ContactLine>
                <ContactLine>
                  <ImageComponent src={callIcon} width="16px" height="16px" />
                  <SpanTag fontSize="xs" color={theme.greyText}>
                    <FormattedText
                      value={primarySection.contact.contactNoNode}
                    />
                  </SpanTag>
                </ContactLine>
              </ContactMobileContent>
            </ContactMobileSection>
          )}

          {isAlternateSectionVisible && alternateSection && (
            <>
              {alternateSection.title && (
                <ContactMobileSection zIndex={2}>
                  <ContactMobileSectionHeader>
                    <ContactMobileTitle>
                      <FormattedText value={alternateSection.title} />
                    </ContactMobileTitle>
                  </ContactMobileSectionHeader>
                  <ContactMobileContent>
                    {hasAlternateContact ? (
                      <>
                        {alternateContact?.emailNode && (
                          <ContactLine>
                            <ImageComponent
                              src={mailIcon}
                              width="16px"
                              height="16px"
                            />
                            <SpanTag fontSize="xs" color={theme.greyText}>
                              <FormattedText
                                value={alternateContact.emailNode}
                              />
                            </SpanTag>
                          </ContactLine>
                        )}

                        {alternateContact?.contactNoNode && (
                          <ContactLine>
                            <ImageComponent
                              src={callIcon}
                              width="16px"
                              height="16px"
                            />
                            <SpanTag fontSize="xs" color={theme.greyText}>
                              <FormattedText
                                value={alternateContact.contactNoNode}
                              />
                            </SpanTag>
                          </ContactLine>
                        )}
                      </>
                    ) : (
                      alternateSection.hint && (
                        <SpanTag fontSize="xs" color={theme.greyText7}>
                          {alternateSection.hint}
                        </SpanTag>
                      )
                    )}
                  </ContactMobileContent>
                </ContactMobileSection>
              )}

              {isActionVisible && alternateAction?.label && (
                <>
                  <CardCtaDivider />
                  <CardCtaButtonList>
                    <TextButton
                      btnTxt={alternateAction.label}
                      onClick={openAlternateContactModal}
                      disabled={!isActionEnabled}
                      fullWidth={true}
                    />
                  </CardCtaButtonList>
                </>
              )}
            </>
          )}
        </>
      )}

      {formConfig && bookingId && primaryEntity && (
        <AddAlternateContactModal
          isOpen={isAlternateContactModalOpen}
          onClose={() => setIsAlternateContactModalOpen(false)}
          formConfig={formConfig}
          modalConfig={modalConfig ?? undefined}
          initialValues={initialAlternateValues}
          bookingId={bookingId}
          primaryEntity={primaryEntity}
          onSave={handleSaveAlternateContact}
          onEndorsementSuccess={refreshOrderDetails}
        />
      )}
    </Card>
  );
};
